import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  BookOpen, AlertTriangle, CheckCircle, XCircle, Star, 
  Target, Lightbulb, Info, Edit, Save, Plus, Trash2, 
  RotateCcw, Loader2, GripVertical, Settings, Copy, Users,
  User, Briefcase, Building, Award, Calendar, Download, FileSpreadsheet,
  BarChart3
} from 'lucide-react';
import { 
  getScoringConfig, 
  updateScoringConfig, 
  addScoringParameter,
  deleteScoringParameter,
  addFatalParameter,
  deleteFatalParameter,
  updateClassificationThresholds,
  resetScoringConfig,
  getScoringConfigs,
  createScoringConfig,
  updateScoringConfigById,
  deleteScoringConfigById,
  duplicateScoringConfig,
  setScoringConfigDefault,
  getScoringAssignments,
  createScoringAssignment,
  createBulkScoringAssignments,
  deleteScoringAssignment,
  getDepartments,
  getRoles,
  getUsers,
  seedTWScoringGuide,
  getAvailableScoringGuides
} from '@/lib/api';
import { toast } from 'sonner';
import ConfigHistoryPanel from '@/components/ConfigHistoryPanel';
import { useAuth } from '@/contexts/AuthContext';

export default function AdminScoringGuide() {
  const { user } = useAuth();
  
  // Check if user is admin (can edit) or auditor/user (read-only)
  const isAdmin = user?.role_id === 'super_admin' || 
                  user?.role_id === 'tenant_admin' || 
                  user?.role === 'super_admin' ||
                  user?.role === 'tenant_admin' ||
                  user?.role === 'admin';
  
  const [activeTab, setActiveTab] = useState('guide');
  const [config, setConfig] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  
  // Multi-config state
  const [configs, setConfigs] = useState([]);
  const [selectedConfigId, setSelectedConfigId] = useState(null);
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [nameSuggestions, setNameSuggestions] = useState([]);
  const [assignments, setAssignments] = useState([]);
  
  // Assignment options
  const [departments, setDepartments] = useState([]);
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);
  
  // Dialogs
  const [showAddParameter, setShowAddParameter] = useState(false);
  const [showAddFatal, setShowAddFatal] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const [showAssignmentDialog, setShowAssignmentDialog] = useState(false);
  const [showBulkAssignDialog, setShowBulkAssignDialog] = useState(false);
  const [editingParameter, setEditingParameter] = useState(null);
  const [deletingItem, setDeletingItem] = useState(null);
  
  // Form states
  const [newParameter, setNewParameter] = useState({
    name: '', weight: 10, description: '', criteria: ['', '', '', ''], tips: ''
  });
  const [newFatal, setNewFatal] = useState({
    violation: '', description: '', fix: ''
  });
  const [thresholds, setThresholds] = useState({
    best_min: 8.5, good_min: 7.0, improvement_min: 5.5, high_alert_max: 5.4
  });
  const [newConfigName, setNewConfigName] = useState('');
  const [newConfigDescription, setNewConfigDescription] = useState('');
  
  // Assignment form
  const [assignmentType, setAssignmentType] = useState('user');
  const [assignmentId, setAssignmentId] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState(new Date().toISOString().split('T')[0]);
  const [bulkAssignmentIds, setBulkAssignmentIds] = useState([]);
  
  // TW Scoring Guide import
  const [isImportingTW, setIsImportingTW] = useState(false);
  const [availableGuides, setAvailableGuides] = useState([]);

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (selectedConfigId && configs.length > 0) {
      const config = configs.find(c => c.id === selectedConfigId);
      setSelectedConfig(config || null);
      setConfig(config || null);
      if (config?.classification_thresholds) {
        setThresholds(config.classification_thresholds);
      }
    }
  }, [selectedConfigId, configs]);

  const loadInitialData = async () => {
    try {
      const [deptRes, roleRes, usersRes, guidesRes] = await Promise.all([
        getDepartments(),
        getRoles(),
        getUsers(),
        getAvailableScoringGuides().catch(() => ({ data: { available_guides: [] } }))
      ]);
      setDepartments(deptRes.data);
      setRoles(roleRes.data);
      setUsers(usersRes.data);
      setAvailableGuides(guidesRes.data.available_guides || []);
      
      // Force select the default config on initial page load
      await loadConfigs(true);
      await loadAssignments();
    } catch (error) {
      console.error('Failed to load initial data:', error);
    }
  };

  const loadConfigs = async (forceSelectDefault = false) => {
    try {
      const res = await getScoringConfigs();
      setConfigs(res.data.configs || []);
      setNameSuggestions(res.data.name_suggestions || []);
      
      // Select the default config on initial load or when forced
      if ((!selectedConfigId || forceSelectDefault) && res.data.configs?.length > 0) {
        const defaultConfig = res.data.configs.find(c => c.is_default) || res.data.configs[0];
        setSelectedConfigId(defaultConfig.id);
        setSelectedConfig(defaultConfig);
        setConfig(defaultConfig);
        if (defaultConfig.classification_thresholds) {
          setThresholds(defaultConfig.classification_thresholds);
        }
      }
    } catch (error) {
      console.error('Failed to load configs:', error);
      // Fallback to legacy config
      try {
        const res = await getScoringConfig();
        setConfig(res.data);
        setThresholds(res.data.classification_thresholds || {
          best_min: 8.5, good_min: 7.0, improvement_min: 5.5, high_alert_max: 5.4
        });
      } catch (e) {
        toast.error('Failed to load scoring configuration');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const loadAssignments = async () => {
    try {
      const res = await getScoringAssignments();
      setAssignments(res.data.assignments || []);
    } catch (error) {
      console.error('Failed to load assignments:', error);
    }
  };
  
  // Import TW Scoring Guide handler
  const handleImportTWGuide = async () => {
    setIsImportingTW(true);
    try {
      const res = await seedTWScoringGuide();
      const wasCreated = res.data.created;
      toast.success(wasCreated ? 'TW Scoring Guide imported successfully!' : 'TW Scoring Guide updated successfully!');
      await loadConfigs();
      // Select the newly imported guide
      if (res.data.config?.id) {
        setSelectedConfigId(res.data.config.id);
      }
    } catch (error) {
      console.error('Failed to import TW Scoring Guide:', error);
      toast.error('Failed to import TW Scoring Guide');
    } finally {
      setIsImportingTW(false);
    }
  };

  const handleSaveConfig = async () => {
    setIsSaving(true);
    try {
      if (selectedConfigId) {
        await updateScoringConfigById(selectedConfigId, {
          ...selectedConfig,
          classification_thresholds: thresholds
        });
      } else {
        await updateScoringConfig({...config, classification_thresholds: thresholds});
      }
      toast.success('Scoring configuration saved successfully');
      setIsEditMode(false);
      await loadConfigs();
    } catch (error) {
      console.error('Failed to save config:', error);
      toast.error('Failed to save configuration');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCreateConfig = async () => {
    if (!newConfigName.trim()) {
      toast.error('Please enter a configuration name');
      return;
    }
    
    try {
      const res = await createScoringConfig({
        name: newConfigName,
        description: newConfigDescription,
        is_default: configs.length === 0
      });
      toast.success('Configuration created');
      setShowCreateDialog(false);
      setNewConfigName('');
      setNewConfigDescription('');
      await loadConfigs();
      setSelectedConfigId(res.data.config.id);
    } catch (error) {
      toast.error('Failed to create configuration');
    }
  };

  const handleDuplicateConfig = async () => {
    if (!newConfigName.trim() || !selectedConfigId) return;
    
    try {
      const res = await duplicateScoringConfig(selectedConfigId, newConfigName);
      toast.success('Configuration duplicated');
      setShowDuplicateDialog(false);
      setNewConfigName('');
      await loadConfigs();
      setSelectedConfigId(res.data.config.id);
    } catch (error) {
      toast.error('Failed to duplicate configuration');
    }
  };

  const handleDeleteConfig = async (configId) => {
    const config = configs.find(c => c.id === configId);
    if (config?.is_default) {
      toast.error('Cannot delete the default configuration');
      return;
    }
    
    if (!confirm('Are you sure you want to delete this configuration?')) return;
    
    try {
      await deleteScoringConfigById(configId);
      toast.success('Configuration deleted');
      await loadConfigs();
      await loadAssignments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete configuration');
    }
  };

  const handleSetDefault = async (configId) => {
    try {
      await setScoringConfigDefault(configId);
      toast.success('Default configuration updated');
      // Update local state immediately to reflect the new default
      setSelectedConfigId(configId);
      await loadConfigs();
    } catch (error) {
      toast.error('Failed to update default');
    }
  };

  const handleCreateAssignment = async () => {
    if (!assignmentId || !selectedConfigId) {
      toast.error('Please select an assignment target');
      return;
    }
    
    try {
      await createScoringAssignment({
        config_id: selectedConfigId,
        assignment_type: assignmentType,
        assignment_id: assignmentId,
        effective_from: new Date(effectiveFrom).toISOString()
      });
      toast.success('Assignment created');
      setShowAssignmentDialog(false);
      setAssignmentId('');
      await loadAssignments();
    } catch (error) {
      toast.error('Failed to create assignment');
    }
  };

  const handleBulkAssignment = async () => {
    if (bulkAssignmentIds.length === 0 || !selectedConfigId) {
      toast.error('Please select at least one target');
      return;
    }
    
    try {
      await createBulkScoringAssignments({
        config_id: selectedConfigId,
        assignment_type: assignmentType,
        assignment_ids: bulkAssignmentIds,
        effective_from: new Date(effectiveFrom).toISOString()
      });
      toast.success(`Assigned to ${bulkAssignmentIds.length} ${assignmentType}s`);
      setShowBulkAssignDialog(false);
      setBulkAssignmentIds([]);
      await loadAssignments();
    } catch (error) {
      toast.error('Failed to create bulk assignments');
    }
  };

  const handleDeleteAssignment = async (assignmentId) => {
    try {
      await deleteScoringAssignment(assignmentId);
      toast.success('Assignment removed');
      await loadAssignments();
    } catch (error) {
      toast.error('Failed to remove assignment');
    }
  };

  const handleAddParameter = async () => {
    try {
      const criteriaArray = newParameter.criteria.filter(c => c.trim() !== '');
      await addScoringParameter({
        ...newParameter,
        criteria: criteriaArray
      });
      toast.success('Parameter added successfully');
      setShowAddParameter(false);
      setNewParameter({ name: '', weight: 10, description: '', criteria: ['', '', '', ''], tips: '' });
      loadConfigs();
    } catch (error) {
      toast.error('Failed to add parameter');
    }
  };

  const handleDeleteParameter = async (paramId) => {
    try {
      await deleteScoringParameter(paramId);
      toast.success('Parameter deleted');
      setDeletingItem(null);
      loadConfigs();
    } catch (error) {
      toast.error('Failed to delete parameter');
    }
  };

  const handleAddFatal = async () => {
    try {
      await addFatalParameter(newFatal);
      toast.success('Fatal parameter added');
      setShowAddFatal(false);
      setNewFatal({ violation: '', description: '', fix: '' });
      loadConfigs();
    } catch (error) {
      toast.error('Failed to add fatal parameter');
    }
  };

  const handleDeleteFatal = async (paramId) => {
    try {
      await deleteFatalParameter(paramId);
      toast.success('Fatal parameter deleted');
      setDeletingItem(null);
      loadConfigs();
    } catch (error) {
      toast.error('Failed to delete fatal parameter');
    }
  };

  const handleReset = async () => {
    try {
      await resetScoringConfig();
      toast.success('Configuration reset to defaults');
      setShowResetConfirm(false);
      loadConfigs();
    } catch (error) {
      toast.error('Failed to reset configuration');
    }
  };

  const getAssignmentOptions = () => {
    switch (assignmentType) {
      case 'user':
        return users.map(u => ({ id: u.id, name: u.name }));
      case 'role':
        return roles.map(r => ({ id: r.id, name: r.name }));
      case 'department':
        return departments.map(d => ({ id: d.id, name: d.name }));
      case 'designation':
        return [...new Set(users.map(u => u.designation).filter(Boolean))].map(d => ({ id: d, name: d }));
      default:
        return [];
    }
  };

  const getAssignmentIcon = (type) => {
    switch (type) {
      case 'user': return <User className="h-4 w-4" />;
      case 'role': return <Briefcase className="h-4 w-4" />;
      case 'department': return <Building className="h-4 w-4" />;
      case 'designation': return <Award className="h-4 w-4" />;
      default: return null;
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-24" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg p-6 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-white/20 rounded-lg">
              <BookOpen className="h-8 w-8" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Call Quality Scoring Guide</h1>
              <p className="text-blue-100 mt-1">
                Multi-model scoring configurations for different teams
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Only show edit controls to admins */}
            {isAdmin ? (
              isEditMode ? (
                <>
                  <Button 
                    variant="secondary"
                    onClick={() => setIsEditMode(false)}
                    className="bg-white/20 hover:bg-white/30 text-white"
                  >
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleSaveConfig}
                    disabled={isSaving}
                    className="bg-white text-blue-600 hover:bg-white/90"
                  >
                    {isSaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                    Save Changes
                  </Button>
                </>
              ) : (
                <Button 
                  onClick={() => setIsEditMode(true)}
                  className="bg-white text-blue-600 hover:bg-white/90"
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Edit Configuration
                </Button>
              )
            ) : (
              <Badge variant="secondary" className="bg-white/20 text-white">
                View Only
              </Badge>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="guide">Scoring Guide</TabsTrigger>
          {isAdmin && <TabsTrigger value="config">Configuration</TabsTrigger>}
          {isAdmin && <TabsTrigger value="assignments">Assignments</TabsTrigger>}
          <TabsTrigger value="history">Change History</TabsTrigger>
        </TabsList>

        {/* Scoring Guide Tab */}
        <TabsContent value="guide" className="space-y-6">
          {/* How to Use This Guide - Comprehensive Introduction */}
          <Card className="border-indigo-200 bg-gradient-to-br from-indigo-50 to-blue-50 dark:from-indigo-950/20 dark:to-blue-950/20">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-indigo-900 dark:text-indigo-300">
                <BookOpen className="h-5 w-5" />
                How to Use This Scoring Guide
              </CardTitle>
              <CardDescription className="text-indigo-700 dark:text-indigo-400">
                Your comprehensive reference for understanding call quality scoring
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-6">
                {/* What This Guide Contains */}
                <div className="space-y-3">
                  <h4 className="font-semibold flex items-center gap-2 text-indigo-800 dark:text-indigo-200">
                    <Target className="h-4 w-4" />
                    What This Guide Contains
                  </h4>
                  <ul className="space-y-2 text-sm text-indigo-700 dark:text-indigo-300">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                      <span><strong>Score Thresholds:</strong> The ranges that determine if a call is rated as Best, Good, Needs Improvement, or High Alert</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                      <span><strong>Scoring Parameters:</strong> Specific criteria evaluated on each call with their weights and evaluation guidelines</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                      <span><strong>Fatal Parameters:</strong> Critical violations that result in automatic call failure regardless of other scores</span>
                    </li>
                  </ul>
                </div>
                
                {/* How Scores Are Calculated */}
                <div className="space-y-3">
                  <h4 className="font-semibold flex items-center gap-2 text-indigo-800 dark:text-indigo-200">
                    <Star className="h-4 w-4" />
                    How Scores Are Calculated
                  </h4>
                  <ul className="space-y-2 text-sm text-indigo-700 dark:text-indigo-300">
                    <li className="flex items-start gap-2">
                      <span className="w-5 h-5 rounded-full bg-indigo-200 dark:bg-indigo-800 flex items-center justify-center text-xs font-bold flex-shrink-0">1</span>
                      <span>Each parameter is scored from 0-10 based on the criteria listed</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-5 h-5 rounded-full bg-indigo-200 dark:bg-indigo-800 flex items-center justify-center text-xs font-bold flex-shrink-0">2</span>
                      <span>Scores are multiplied by their weights and summed</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-5 h-5 rounded-full bg-indigo-200 dark:bg-indigo-800 flex items-center justify-center text-xs font-bold flex-shrink-0">3</span>
                      <span>Final weighted average determines the classification</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-5 h-5 rounded-full bg-red-200 dark:bg-red-800 flex items-center justify-center text-xs font-bold flex-shrink-0">!</span>
                      <span>Any fatal parameter triggers auto-fail (score override)</span>
                    </li>
                  </ul>
                </div>
              </div>
              
              {/* Quick Tips */}
              <div className="mt-4 p-3 bg-amber-100/50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                <div className="flex items-start gap-2">
                  <Lightbulb className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-amber-800 dark:text-amber-200">Pro Tip for Improvement</p>
                    <p className="text-sm text-amber-700 dark:text-amber-300">
                      Focus on parameters with higher weights first - improving these will have the biggest impact on your overall score. 
                      Review the criteria carefully and practice incorporating them naturally into your calls.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Config Selector */}
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <Select value={selectedConfigId || ''} onValueChange={setSelectedConfigId}>
                  <SelectTrigger className="w-[300px]">
                    <SelectValue placeholder="Select configuration" />
                  </SelectTrigger>
                  <SelectContent>
                    {configs.map(config => (
                      <SelectItem key={config.id} value={config.id}>
                        <span className="flex items-center gap-2">
                          {config.name}
                          {config.is_default && <Badge variant="outline" className="ml-2">Default</Badge>}
                          {config.source === 'SKILL NEXA.xlsx' && <Badge variant="secondary" className="ml-2">TW Guide</Badge>}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <Button variant="outline" onClick={() => setShowCreateDialog(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  New Config
                </Button>
                
                {/* Import TW Scoring Guide Button */}
                <Button 
                  variant="outline" 
                  onClick={handleImportTWGuide}
                  disabled={isImportingTW}
                  className="border-blue-300 text-blue-700 hover:bg-blue-50"
                  title="Import/Update the TW Scoring Guide from SKILL NEXA framework"
                >
                  {isImportingTW ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <FileSpreadsheet className="h-4 w-4 mr-2" />
                  )}
                  {configs.some(c => c.name === 'TW Scoring Guide') ? 'Update TW Guide' : 'Import TW Guide'}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* TW Scoring Guide Info Banner */}
          {selectedConfig?.source === 'SKILL NEXA.xlsx' && (
            <Card className="border-blue-200 bg-blue-50/50">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <FileSpreadsheet className="h-5 w-5 text-blue-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-blue-900">TW Scoring Guide Active</p>
                    <p className="text-sm text-blue-700">
                      This guide is based on the SKILL NEXA.xlsx Call Management Framework with {selectedConfig.parameters?.length || 0} scoring parameters, 
                      {selectedConfig.fatal_parameters?.length || 0} fatal parameters, and {selectedConfig.categories?.length || 0} categories.
                    </p>
                    {selectedConfig.version && (
                      <p className="text-xs text-blue-600 mt-1">Version: {selectedConfig.version}</p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Classification Thresholds */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="h-5 w-5 text-blue-600" />
                Classification Thresholds
              </CardTitle>
              <CardDescription>
                Score ranges that determine call classification. These thresholds help you understand how your calls are rated.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Explanation Banner */}
              <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border text-sm text-muted-foreground">
                <p className="flex items-center gap-2">
                  <Info className="h-4 w-4 flex-shrink-0" />
                  <span>Your call's final weighted score determines its classification. Aim for <strong className="text-emerald-600">Best (≥{thresholds.best_min})</strong> to demonstrate excellent performance.</span>
                </p>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-4 bg-emerald-50 dark:bg-emerald-950/30 rounded-lg border-2 border-emerald-200 dark:border-emerald-800">
                  <div className="flex items-center gap-2 mb-2">
                    <Star className="h-5 w-5 text-emerald-600" />
                    <span className="font-semibold text-emerald-700 dark:text-emerald-400">Best</span>
                  </div>
                  <p className="text-2xl font-bold text-emerald-600">≥ {thresholds.best_min}</p>
                  <p className="text-xs text-emerald-600/80 mt-1">Outstanding performance - all criteria met excellently</p>
                  {isEditMode && (
                    <Input
                      type="number"
                      step="0.1"
                      value={thresholds.best_min}
                      onChange={(e) => setThresholds({...thresholds, best_min: parseFloat(e.target.value)})}
                      className="mt-2"
                    />
                  )}
                </div>
                <div className="p-4 bg-blue-50 dark:bg-blue-950/30 rounded-lg border-2 border-blue-200 dark:border-blue-800">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle className="h-5 w-5 text-blue-600" />
                    <span className="font-semibold text-blue-700 dark:text-blue-400">Good</span>
                  </div>
                  <p className="text-2xl font-bold text-blue-600">{thresholds.good_min} - {thresholds.best_min - 0.1}</p>
                  <p className="text-xs text-blue-600/80 mt-1">Solid performance - meets expectations consistently</p>
                  {isEditMode && (
                    <Input
                      type="number"
                      step="0.1"
                      value={thresholds.good_min}
                      onChange={(e) => setThresholds({...thresholds, good_min: parseFloat(e.target.value)})}
                      className="mt-2"
                    />
                  )}
                </div>
                <div className="p-4 bg-amber-50 dark:bg-amber-950/30 rounded-lg border-2 border-amber-200 dark:border-amber-800">
                  <div className="flex items-center gap-2 mb-2">
                    <Info className="h-5 w-5 text-amber-600" />
                    <span className="font-semibold text-amber-700 dark:text-amber-400">Improvement</span>
                  </div>
                  <p className="text-2xl font-bold text-amber-600">{thresholds.improvement_min} - {thresholds.good_min - 0.1}</p>
                  <p className="text-xs text-amber-600/80 mt-1">Some gaps identified - focus on specific areas</p>
                  {isEditMode && (
                    <Input
                      type="number"
                      step="0.1"
                      value={thresholds.improvement_min}
                      onChange={(e) => setThresholds({...thresholds, improvement_min: parseFloat(e.target.value)})}
                      className="mt-2"
                    />
                  )}
                </div>
                <div className="p-4 bg-red-50 dark:bg-red-950/30 rounded-lg border-2 border-red-200 dark:border-red-800">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle className="h-5 w-5 text-red-600" />
                    <span className="font-semibold text-red-700 dark:text-red-400">High Alert</span>
                  </div>
                  <p className="text-2xl font-bold text-red-600">≤ {thresholds.high_alert_max}</p>
                  <p className="text-xs text-red-600/80 mt-1">Critical issues detected - requires immediate attention</p>
                  {isEditMode && (
                    <Input
                      type="number"
                      step="0.1"
                      value={thresholds.high_alert_max}
                      onChange={(e) => setThresholds({...thresholds, high_alert_max: parseFloat(e.target.value)})}
                      className="mt-2"
                    />
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Scoring Parameters */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5 text-blue-600" />
                  Scoring Parameters
                </CardTitle>
                <CardDescription>
                  Each parameter is evaluated on a scale of 0-10. Higher weights mean greater impact on your final score.
                </CardDescription>
              </div>
              {isEditMode && (
                <Button onClick={() => setShowAddParameter(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Parameter
                </Button>
              )}
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {(selectedConfig?.parameters || config?.parameters || []).map((param, idx) => (
                  <div 
                    key={param.id || idx} 
                    className="p-4 border rounded-lg hover:border-blue-200 transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <Badge className="bg-blue-100 text-blue-800">
                            Weight: {param.weight}
                          </Badge>
                          <h4 className="font-semibold text-lg">{param.name}</h4>
                        </div>
                        <p className="text-muted-foreground mb-3">{param.description}</p>
                        
                        {param.criteria?.length > 0 && (
                          <div className="mb-3">
                            <p className="text-sm font-medium text-muted-foreground mb-2">Criteria:</p>
                            <ul className="space-y-1">
                              {param.criteria.map((c, i) => (
                                <li key={i} className="flex items-start gap-2 text-sm">
                                  <CheckCircle className="h-4 w-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                                  {c}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        
                        {param.tips && (
                          <div className="flex items-start gap-2 p-2 bg-amber-50 rounded text-sm">
                            <Lightbulb className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                            <span className="text-amber-800">{param.tips}</span>
                          </div>
                        )}
                      </div>
                      
                      {isEditMode && (
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => setDeletingItem({type: 'parameter', id: param.id, name: param.name})}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Fatal Parameters */}
          <Card className="border-red-200">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2 text-red-700">
                  <XCircle className="h-5 w-5" />
                  Fatal Parameters (Auto-Fail)
                </CardTitle>
                <CardDescription>Violations that result in automatic call failure</CardDescription>
              </div>
              {isEditMode && (
                <Button variant="destructive" onClick={() => setShowAddFatal(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Fatal Parameter
                </Button>
              )}
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {(selectedConfig?.fatal_parameters || config?.fatal_parameters || []).map((param, idx) => (
                  <div 
                    key={param.id || idx} 
                    className="p-4 bg-red-50 border border-red-200 rounded-lg"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <AlertTriangle className="h-5 w-5 text-red-600" />
                          <h4 className="font-semibold text-red-700">{param.violation}</h4>
                        </div>
                        <p className="text-red-700 mb-2">{param.description}</p>
                        {param.fix && (
                          <div className="flex items-start gap-2 p-2 bg-white rounded text-sm">
                            <CheckCircle className="h-4 w-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                            <span><strong>Fix:</strong> {param.fix}</span>
                          </div>
                        )}
                      </div>
                      
                      {isEditMode && (
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => setDeletingItem({type: 'fatal', id: param.id, name: param.violation})}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Configuration Tab */}
        <TabsContent value="config" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Settings className="h-5 w-5" />
                  Scoring Configurations
                </span>
                <Button onClick={() => setShowCreateDialog(true)} size="sm">
                  <Plus className="h-4 w-4 mr-2" />
                  New Configuration
                </Button>
              </CardTitle>
              <CardDescription>
                Manage multiple scoring configurations for different teams
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4 mb-6">
                <Select value={selectedConfigId || ''} onValueChange={setSelectedConfigId}>
                  <SelectTrigger className="w-[300px]">
                    <SelectValue placeholder="Select configuration" />
                  </SelectTrigger>
                  <SelectContent>
                    {configs.map(config => (
                      <SelectItem key={config.id} value={config.id}>
                        <span className="flex items-center gap-2">
                          {config.name}
                          {config.is_default && <Badge variant="outline" className="ml-2">Default</Badge>}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                {selectedConfigId && (
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => {
                        setNewConfigName(`${selectedConfig?.name || 'Config'} Copy`);
                        setShowDuplicateDialog(true);
                      }}
                    >
                      <Copy className="h-4 w-4 mr-1" />
                      Duplicate
                    </Button>
                    {!selectedConfig?.is_default && (
                      <>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleSetDefault(selectedConfigId)}
                        >
                          <Star className="h-4 w-4 mr-1" />
                          Set Default
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          className="text-red-600"
                          onClick={() => handleDeleteConfig(selectedConfigId)}
                        >
                          <Trash2 className="h-4 w-4 mr-1" />
                          Delete
                        </Button>
                      </>
                    )}
                  </div>
                )}
              </div>

              {selectedConfig && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Configuration Name</Label>
                      <Input 
                        value={selectedConfig.name || ''} 
                        onChange={(e) => setSelectedConfig({...selectedConfig, name: e.target.value})}
                        disabled={!isEditMode}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Description</Label>
                      <Input 
                        value={selectedConfig.description || ''} 
                        onChange={(e) => setSelectedConfig({...selectedConfig, description: e.target.value})}
                        placeholder="Optional description"
                        disabled={!isEditMode}
                      />
                    </div>
                  </div>
                  
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <h4 className="font-medium mb-2">Configuration Stats</h4>
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <span className="text-muted-foreground">Parameters:</span>
                        <span className="ml-2 font-medium">{selectedConfig.parameters?.length || 0}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Fatal Parameters:</span>
                        <span className="ml-2 font-medium">{selectedConfig.fatal_parameters?.length || 0}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Assignments:</span>
                        <span className="ml-2 font-medium">
                          {assignments.filter(a => a.config_id === selectedConfigId).length}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Assignments Tab */}
        <TabsContent value="assignments" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Configuration Assignments
                </span>
                <div className="flex gap-2">
                  <Button onClick={() => setShowAssignmentDialog(true)} size="sm">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Assignment
                  </Button>
                  <Button onClick={() => setShowBulkAssignDialog(true)} size="sm" variant="outline">
                    <Users className="h-4 w-4 mr-2" />
                    Bulk Assign
                  </Button>
                </div>
              </CardTitle>
              <CardDescription>
                Assign scoring configurations to users, roles, designations, or departments.
                Priority: User {'>'} Role {'>'} Designation {'>'} Department {'>'} Default
              </CardDescription>
            </CardHeader>
            <CardContent>
              {assignments.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No assignments yet. All users are using the default configuration.</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Type</TableHead>
                      <TableHead>Assigned To</TableHead>
                      <TableHead>Configuration</TableHead>
                      <TableHead>Effective From</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {assignments.map(assignment => (
                      <TableRow key={assignment.id}>
                        <TableCell>
                          <Badge variant="outline" className="flex items-center gap-1 w-fit">
                            {getAssignmentIcon(assignment.assignment_type)}
                            {assignment.assignment_type}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-medium">
                          {assignment.assignment_name || assignment.assignment_id}
                        </TableCell>
                        <TableCell>
                          <Badge>{assignment.config_name}</Badge>
                        </TableCell>
                        <TableCell>
                          {new Date(assignment.effective_from).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => handleDeleteAssignment(assignment.id)}
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history" className="space-y-6">
          <ConfigHistoryPanel configType="scoring" />
        </TabsContent>
      </Tabs>

      {/* Create Config Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Scoring Configuration</DialogTitle>
            <DialogDescription>
              Create a new configuration with custom scoring parameters
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Configuration Name</Label>
              <Input 
                value={newConfigName} 
                onChange={(e) => setNewConfigName(e.target.value)}
                placeholder="Enter configuration name"
              />
              <div className="flex flex-wrap gap-2 mt-2">
                {nameSuggestions.map(suggestion => (
                  <Badge 
                    key={suggestion} 
                    variant="outline" 
                    className="cursor-pointer hover:bg-muted"
                    onClick={() => setNewConfigName(suggestion)}
                  >
                    {suggestion}
                  </Badge>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Description (Optional)</Label>
              <Textarea 
                value={newConfigDescription} 
                onChange={(e) => setNewConfigDescription(e.target.value)}
                placeholder="Describe this configuration"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateConfig}>Create Configuration</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Duplicate Config Dialog */}
      <Dialog open={showDuplicateDialog} onOpenChange={setShowDuplicateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Duplicate Configuration</DialogTitle>
            <DialogDescription>
              Create a copy of "{selectedConfig?.name}" with a new name
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>New Configuration Name</Label>
              <Input 
                value={newConfigName} 
                onChange={(e) => setNewConfigName(e.target.value)}
                placeholder="Enter new configuration name"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDuplicateDialog(false)}>Cancel</Button>
            <Button onClick={handleDuplicateConfig}>Duplicate</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assignment Dialog */}
      <Dialog open={showAssignmentDialog} onOpenChange={setShowAssignmentDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Assignment</DialogTitle>
            <DialogDescription>
              Assign the selected scoring configuration to a user, role, designation, or department
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Configuration</Label>
              <Select value={selectedConfigId || ''} onValueChange={setSelectedConfigId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select configuration" />
                </SelectTrigger>
                <SelectContent>
                  {configs.map(config => (
                    <SelectItem key={config.id} value={config.id}>{config.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Assignment Type</Label>
              <Select value={assignmentType} onValueChange={(v) => { setAssignmentType(v); setAssignmentId(''); }}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">
                    <span className="flex items-center gap-2"><User className="h-4 w-4" /> User</span>
                  </SelectItem>
                  <SelectItem value="role">
                    <span className="flex items-center gap-2"><Briefcase className="h-4 w-4" /> Role</span>
                  </SelectItem>
                  <SelectItem value="designation">
                    <span className="flex items-center gap-2"><Award className="h-4 w-4" /> Designation</span>
                  </SelectItem>
                  <SelectItem value="department">
                    <span className="flex items-center gap-2"><Building className="h-4 w-4" /> Department</span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Assign To</Label>
              <Select value={assignmentId} onValueChange={setAssignmentId}>
                <SelectTrigger>
                  <SelectValue placeholder={`Select ${assignmentType}`} />
                </SelectTrigger>
                <SelectContent>
                  {getAssignmentOptions().map(option => (
                    <SelectItem key={option.id} value={option.id}>{option.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Effective From</Label>
              <Input 
                type="date" 
                value={effectiveFrom}
                onChange={(e) => setEffectiveFrom(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignmentDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateAssignment}>Create Assignment</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Assignment Dialog */}
      <Dialog open={showBulkAssignDialog} onOpenChange={setShowBulkAssignDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Bulk Assignment</DialogTitle>
            <DialogDescription>
              Assign a configuration to multiple roles, designations, or departments at once
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Configuration</Label>
              <Select value={selectedConfigId || ''} onValueChange={setSelectedConfigId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select configuration" />
                </SelectTrigger>
                <SelectContent>
                  {configs.map(config => (
                    <SelectItem key={config.id} value={config.id}>{config.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Assignment Type</Label>
              <Select value={assignmentType} onValueChange={(v) => { setAssignmentType(v); setBulkAssignmentIds([]); }}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="role">Roles</SelectItem>
                  <SelectItem value="designation">Designations</SelectItem>
                  <SelectItem value="department">Departments</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Select {assignmentType}s</Label>
              <div className="border rounded-lg p-3 max-h-48 overflow-y-auto space-y-2">
                {getAssignmentOptions().map(option => (
                  <div key={option.id} className="flex items-center space-x-2">
                    <Checkbox 
                      id={`bulk-scoring-${option.id}`}
                      checked={bulkAssignmentIds.includes(option.id)}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setBulkAssignmentIds([...bulkAssignmentIds, option.id]);
                        } else {
                          setBulkAssignmentIds(bulkAssignmentIds.filter(id => id !== option.id));
                        }
                      }}
                    />
                    <Label htmlFor={`bulk-scoring-${option.id}`} className="text-sm cursor-pointer">
                      {option.name}
                    </Label>
                  </div>
                ))}
              </div>
              <p className="text-sm text-muted-foreground">{bulkAssignmentIds.length} selected</p>
            </div>
            
            <div className="space-y-2">
              <Label>Effective From</Label>
              <Input 
                type="date" 
                value={effectiveFrom}
                onChange={(e) => setEffectiveFrom(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulkAssignDialog(false)}>Cancel</Button>
            <Button onClick={handleBulkAssignment} disabled={bulkAssignmentIds.length === 0}>
              Assign to {bulkAssignmentIds.length} {assignmentType}s
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Parameter Dialog */}
      <Dialog open={showAddParameter} onOpenChange={setShowAddParameter}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Scoring Parameter</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Parameter Name</Label>
                <Input 
                  value={newParameter.name}
                  onChange={(e) => setNewParameter({...newParameter, name: e.target.value})}
                  placeholder="e.g., Greeting Quality"
                />
              </div>
              <div className="space-y-2">
                <Label>Weight (1-20)</Label>
                <Input 
                  type="number"
                  min="1"
                  max="20"
                  value={newParameter.weight}
                  onChange={(e) => setNewParameter({...newParameter, weight: parseInt(e.target.value) || 10})}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea 
                value={newParameter.description}
                onChange={(e) => setNewParameter({...newParameter, description: e.target.value})}
                placeholder="What does this parameter evaluate?"
              />
            </div>
            <div className="space-y-2">
              <Label>Criteria (one per line)</Label>
              {newParameter.criteria.map((c, i) => (
                <Input 
                  key={i}
                  value={c}
                  onChange={(e) => {
                    const newCriteria = [...newParameter.criteria];
                    newCriteria[i] = e.target.value;
                    setNewParameter({...newParameter, criteria: newCriteria});
                  }}
                  placeholder={`Criterion ${i + 1}`}
                />
              ))}
            </div>
            <div className="space-y-2">
              <Label>Tips (optional)</Label>
              <Input 
                value={newParameter.tips}
                onChange={(e) => setNewParameter({...newParameter, tips: e.target.value})}
                placeholder="Helpful tip for agents"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddParameter(false)}>Cancel</Button>
            <Button onClick={handleAddParameter}>Add Parameter</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Fatal Parameter Dialog */}
      <Dialog open={showAddFatal} onOpenChange={setShowAddFatal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Fatal Parameter</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Violation Name</Label>
              <Input 
                value={newFatal.violation}
                onChange={(e) => setNewFatal({...newFatal, violation: e.target.value})}
                placeholder="e.g., Privacy Breach"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea 
                value={newFatal.description}
                onChange={(e) => setNewFatal({...newFatal, description: e.target.value})}
                placeholder="What constitutes this violation?"
              />
            </div>
            <div className="space-y-2">
              <Label>How to Fix</Label>
              <Textarea 
                value={newFatal.fix}
                onChange={(e) => setNewFatal({...newFatal, fix: e.target.value})}
                placeholder="How can agents avoid this?"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddFatal(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleAddFatal}>Add Fatal Parameter</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deletingItem} onOpenChange={() => setDeletingItem(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deletingItem?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the {deletingItem?.type}.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={() => {
                if (deletingItem?.type === 'parameter') {
                  handleDeleteParameter(deletingItem.id);
                } else if (deletingItem?.type === 'fatal') {
                  handleDeleteFatal(deletingItem.id);
                }
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
